import { FC, useState } from "react";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import {
  CameraPhotoWithCommentType,
  DriverCamera,
  DriverFlowBottomButton,
} from "../blocks";
import { DsText } from "../components/ui";
import { useAddNote } from "../data/mutations/noteMutations";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUploadServicePhotoMutation } from "../data/mutations/storageMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { TURNAROUND_FEE_NOTE } from "../domain/constants";
import { JobServiceTaskBaseType } from "../domain/task";
import { dateAsDayjs, generateFirebaseTimestamp } from "../utils/date";
import { DriverTopActionButton } from "./DriverTopActionButton";

const DriverAddPhotoObstruction: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const { mutate: uploadServicePhoto, isPending: isUploadingServicePhoto } =
    useUploadServicePhotoMutation();

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const { mutate: addNote, isPending: isAddingNote } = useAddNote();
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const openCamera = () => setIsCameraOpen(true);

  const closeCamera = () => setIsCameraOpen(false);

  const tomorrowDueAt = dateAsDayjs().add(1, "day");

  const uploadPhoto = ({ base64PhotoString }: CameraPhotoWithCommentType) => {
    uploadServicePhoto(
      {
        jobId,
        serviceId,
        base64PhotoString,
        baseName: "turnaround",
      },
      {
        onSuccess: path => {
          updateService({
            jobId,
            serviceId,
            driverFlowStatus: TaskDriverStatusEnum.TASK_REVIEW,
            turnaroundPhoto: {
              photoUrl: path,
              comment: serviceData?.turnaroundPhoto?.comment ?? "",
            },
            dueAt: generateFirebaseTimestamp(tomorrowDueAt.toDate()),
            siteDropPhoto: null,
          });

          updateTask({
            jobId,
            serviceId,
            taskId,
            dueAt: generateFirebaseTimestamp(tomorrowDueAt.toDate()),
            driverFlowStatus: TaskDriverStatusEnum.TASK_REVIEW,
          });
          addNote({
            jobId,
            serviceId,
            taskId,
            note: TURNAROUND_FEE_NOTE,
          });
        },
      },
    );
  };

  const isLoading = isUpdatingService || isUpdatingTask || isAddingNote;

  const isNextButtonLoading =
    isUploadingServicePhoto ||
    (isLoading &&
      variables?.driverFlowStatus ===
        TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN);
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN,
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {isCameraOpen ? (
        <DriverCamera
          mode="single-photo"
          onChoosePhoto={photo => {
            setIsCameraOpen(false);
            void uploadPhoto(photo);
          }}
          onCancelClick={closeCamera}
          canAddComment={false}
        />
      ) : (
        <>
          <DriverTopActionButton
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />
          <div className="flex flex-grow w-full flex-col justify-center">
            <DsText variant="pageTitle">
              {t("driver_add_photo_obstruction.title")}
            </DsText>
          </div>
          <DriverFlowBottomButton
            onNextButtonClick={openCamera}
            isNextButtonLoading={isUploadingServicePhoto || isNextButtonLoading}
            nextButtonTitle="take_photo"
            previousButtonTitle="driver_add_photo_obstruction.back_button"
            onPreviousButtonClick={goToPreviousStep}
            isPreviousButtonLoading={isPreviousButtonLoading}
          />
        </>
      )}
    </div>
  );
};

export { DriverAddPhotoObstruction };
